import { lazy, Suspense, useEffect, useState, type ComponentType, type LazyExoticComponent } from 'react'
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router-dom'
import { fetchAuthConfig } from './auth'
import { WorkspaceContext, type OpenWorkspace } from './components/workspaceContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ModuleBadge } from './components/ModuleBadge'
import { Redirect, type RedirectContext } from './components/Redirect'
import { ThemeToggle, UserMenu } from './components/HeaderControls'
import { legacyAssessmentTarget, paths } from './routes'
import { RequireAuth } from './components/RequireAuth'
import { PortalLayout } from './pages/portal/PortalLayout'
// The two anonymous pages load eagerly; everything behind RequireAuth is lazy
// (below), so the public /newcustomer + /login bundle doesn't ship the whole
// consultant app.
import { ForgotPassword } from './pages/ForgotPassword'
import { Login } from './pages/Login'
import { SetPassword } from './pages/SetPassword'
import { NewCustomer } from './pages/NewCustomer'
import { QuestionnaireHelp } from './pages/QuestionnaireHelp'
// Self-hosted webfonts (no external requests). Display face for headings, body
// face for prose, mono for ids/paths/diffs. See styles.css `--font-*` tokens.
import '@fontsource-variable/bricolage-grotesque/index.css'
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import './styles.css'

/** `React.lazy` over a NAMED export (the pages export named components). A
 *  chunk that fails to load after a deploy is handled by main.tsx's one-time
 *  `vite:preloadError` reload. */
function lazyNamed<K extends string, M extends Record<K, ComponentType<any>>>(
  load: () => Promise<M>,
  key: K,
): LazyExoticComponent<M[K]> {
  return lazy(() => load().then((m) => ({ default: m[key] })))
}

const Admin = lazyNamed(() => import('./pages/Admin'), 'Admin')
const AdminCompanies = lazyNamed(() => import('./pages/AdminCompanies'), 'AdminCompanies')
const AdminHome = lazyNamed(() => import('./pages/AdminHome'), 'AdminHome')
const AdminPlaybooks = lazyNamed(() => import('./pages/AdminPlaybooks'), 'AdminPlaybooks')
const AdminSettings = lazyNamed(() => import('./pages/AdminSettings'), 'AdminSettings')
const Analysis = lazyNamed(() => import('./pages/Analysis'), 'Analysis')
const AssistantChat = lazyNamed(() => import('./pages/AssistantChat'), 'AssistantChat')
const CompanyAccess = lazyNamed(() => import('./pages/CompanyAccess'), 'CompanyAccess')
const CompanyHub = lazyNamed(() => import('./pages/CompanyHub'), 'CompanyHub')
const CompanyList = lazyNamed(() => import('./pages/CompanyList'), 'CompanyList')
const CompanyProfile = lazyNamed(() => import('./pages/CompanyProfile'), 'CompanyProfile')
const AceOpportunityNew = lazyNamed(() => import('./pages/AceOpportunityNew'), 'AceOpportunityNew')
const AwsInventory = lazyNamed(() => import('./pages/AwsInventory'), 'AwsInventory')
const AwsScan = lazyNamed(() => import('./pages/AwsScan'), 'AwsScan')
const FtrWorkspace = lazyNamed(() => import('./pages/FtrWorkspace'), 'FtrWorkspace')
const Help = lazyNamed(() => import('./pages/Help'), 'Help')
const Intake = lazyNamed(() => import('./pages/Intake'), 'Intake')
const Proposals = lazyNamed(() => import('./pages/Proposals'), 'Proposals')
const ResearchReview = lazyNamed(() => import('./pages/ResearchReview'), 'ResearchReview')
const Submission = lazyNamed(() => import('./pages/Submission'), 'Submission')
const TranscriptReview = lazyNamed(() => import('./pages/TranscriptReview'), 'TranscriptReview')
const UseCases = lazyNamed(() => import('./pages/UseCases'), 'UseCases')
const PortalAws = lazyNamed(() => import('./pages/portal/PortalAws'), 'PortalAws')
const PortalHome = lazyNamed(() => import('./pages/portal/PortalHome'), 'PortalHome')
const PortalQuestionnaire = lazyNamed(() => import('./pages/portal/PortalQuestionnaire'), 'PortalQuestionnaire')
const PortalTeam = lazyNamed(() => import('./pages/portal/PortalTeam'), 'PortalTeam')
const PortalWafr = lazyNamed(() => import('./pages/portal/PortalWafr'), 'PortalWafr')
const PortalProfile = lazyNamed(() => import('./pages/portal/PortalProfile'), 'PortalProfile')
const WafrQuestionnaire = lazyNamed(() => import('./pages/WafrQuestionnaire'), 'WafrQuestionnaire')
const WafrWorkspace = lazyNamed(() => import('./pages/WafrWorkspace'), 'WafrWorkspace')

/** Backoff between config-fetch retries; after the last one a still-failing
 *  fetch opens the gate (a hiccup shouldn't lock the app). */
const CONFIG_RETRY_DELAYS_MS = [1000, 3000]

/** Whether the app is configured (`null` = not known yet). Retries a failed
 *  fetch, and re-checks on every navigation until the answer is `true` — so an
 *  admin who finishes setup at /admin/settings isn't left behind a gate that
 *  was evaluated once at mount. */
function useConfigured(pathname: string): boolean | null {
  const [configured, setConfigured] = useState<boolean | null>(null)
  const settled = configured === true
  useEffect(() => {
    if (settled) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const attempt = (n: number) => {
      fetchAuthConfig()
        .then((c) => {
          if (!cancelled) setConfigured(c.configured)
        })
        .catch(() => {
          if (cancelled) return
          const delay = CONFIG_RETRY_DELAYS_MS[n]
          if (delay === undefined) {
            // Out of retries: keep a known `false`, otherwise don't lock the app.
            setConfigured((prev) => prev ?? true)
            return
          }
          timer = setTimeout(() => attempt(n + 1), delay)
        })
    }
    attempt(0)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [pathname, settled])
  return configured
}

/** The assistant is company-scoped now ("chat with company"); the old
 *  in-module chat URL forwards there with the AI context preselected and the
 *  ?prefill=&send=1 hand-off params preserved. */
function moduleAssistantTarget({ slug, search }: RedirectContext): string {
  const params = new URLSearchParams(search)
  params.set('context', 'ai-assessment')
  return `${paths.assistant(slug)}?${params.toString()}`
}

/** Bookmarked pre-restructure URLs: '/assessments/:slug/*' → the AI module
 *  under its company (same slug — companies re-key 1:1). Query strings are
 *  preserved so deep links (?section=, ?prefill=&send=1) keep working.
 *
 *  slug + rest are derived from the PATHNAME, not the route's params: this
 *  element renders inside the descendant <Routes> under the outer `path="*"`
 *  route, and when the splat-less '/assessments/:slug' pattern matches, the
 *  OUTER route's '*' param ("assessments/{slug}") leaks through
 *  useParams()['*'] — which used to redirect to '/companies/{slug}/
 *  ai-assessment/assessments/{slug}' (Not found) for bare '/assessments/{slug}'
 *  bookmarks. */
function legacyAssessmentRedirectTarget({ pathname, search }: RedirectContext): string {
  const m = pathname.match(/^\/assessments\/([^/]+)\/?(.*)$/)
  return legacyAssessmentTarget(m?.[1] ?? '', m?.[2] ?? '', search)
}

/** Monochrome chat-bubble icon (currentColor — white on the dark header). */
function ChatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  )
}

function NotConfigured() {
  return (
    <div className="empty-state" data-testid="not-configured" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <h2 style={{ marginTop: 0 }}>Not configured yet</h2>
      <p className="muted" style={{ maxWidth: '34rem', margin: '0.5rem auto 1.25rem' }}>
        The app needs its AWS, web-research, and storage settings before it can work. An admin must
        finish setup before assessments can be created or opened.
      </p>
      <Link to="/admin/settings" className="primary" data-testid="goto-settings-btn"
        style={{ display: 'inline-block', padding: '0.5rem 1rem', borderRadius: '6px', textDecoration: 'none' }}>
        Go to Settings →
      </Link>
    </div>
  )
}

function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  // null = still checking; don't flash the gate before we know.
  const configured = useConfigured(location.pathname)
  // The open workspace (company + module), published by the status banners /
  // hub pages so the brand row reads "AllCode | Assessments — {company} ·
  // [Module]" with the chat shortcut beside it when the AI module is open.
  const [workspace, setWorkspace] = useState<OpenWorkspace | null>(null)
  // Admin (where you fix it) + Help stay reachable while unconfigured.
  const exempt = location.pathname.startsWith('/admin') || location.pathname.startsWith('/help')
  const blocked = configured === false && !exempt
  return (
    <div className="app-layout">
      <header className="app-header">
        <Link to="/" className="brand" aria-label="AllCode home">
          <img src="/brand/allcode-logo.png" alt="AllCode" />
          <span className="product">Assessments</span>
        </Link>
        {workspace && (
          <>
            <span className="app-header__assessment" data-testid="header-assessment-name">
              <Link to={paths.company(workspace.companySlug)} className="app-header__company">
                - {workspace.companyName}
              </Link>
            </span>
            {workspace.module && <ModuleBadge module={workspace.module} size="sm" />}
            {/* Chat with this company — one click from anywhere in its workspace,
                preselecting the open module as the chat context. */}
            <Link
              to={paths.assistant(workspace.companySlug, workspace.module ?? undefined)}
              className="header-chat"
              data-testid="header-chat-btn"
              title="Chat with this company"
              aria-label="Chat with this company"
            >
              <ChatIcon />
            </Link>
          </>
        )}
        <nav className="app-header__nav">
          <ThemeToggle />
          <Link to="/help" data-testid="help-nav-link">Help</Link>
          <UserMenu />
        </nav>
      </header>
      <main className="app-main">
        <WorkspaceContext.Provider value={setWorkspace}>
          {blocked ? <NotConfigured /> : children}
        </WorkspaceContext.Provider>
      </main>
      <footer className="app-footer" data-testid="app-footer">
        © {new Date().getFullYear()} AllCode
      </footer>
    </div>
  )
}

export default function App() {
  return (
    // Outer boundary: a safety net if anything above the pages (router,
    // RequireAuth, Layout) throws.
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public customer self-onboarding — deliberately outside RequireAuth. */}
          <Route path={paths.newCustomer()} element={<NewCustomer />} />
          {/* Public help linked from the emailed questionnaire — static schema, no customer data. */}
          <Route path={paths.questionnaireHelp()} element={<QuestionnaireHelp />} />
          {/* Public customer-account pages (invite/reset links land here). */}
          <Route path={paths.forgotPassword()} element={<ForgotPassword />} />
          <Route path={paths.setPassword()} element={<SetPassword />} />
          {/* The customer portal — its own guard (customers only) and chrome. */}
          <Route
            path="/portal/*"
            element={
              <RequireAuth kind="customer">
                <PortalLayout>
                  <PageBoundary>
                    <PortalRoutes />
                  </PageBoundary>
                </PortalLayout>
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={
              <RequireAuth kind="staff">
                <Layout>
                  <PageBoundary>
                    <PageRoutes />
                  </PageBoundary>
                </Layout>
              </RequireAuth>
            }
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

/** Inner boundary: a thrown error inside a page renders the fallback in
 *  <main> while keeping the header + nav usable. Keyed by pathname so
 *  navigating away — e.g. after a lazy chunk failed to load, where "Try again"
 *  can't help because React.lazy caches the rejection — mounts a fresh
 *  boundary instead of leaving the error UI stuck on every later page. */
function PageBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  // Outer: the default panel (Reload + Try again). Inner: catches chunk-load
  // failures itself and rethrows everything else up to the outer one.
  return (
    <ErrorBoundary key={pathname}>
      <ErrorBoundary fallback={chunkErrorFallback}>{children}</ErrorBoundary>
    </ErrorBoundary>
  )
}

/** Re-throws during render so the next boundary up handles the error. */
function Rethrow({ error }: { error: Error }): never {
  throw error
}

/** A lazy page whose chunk didn't load (Vite's "Failed to fetch dynamically
 *  imported module" / "Importing a module script failed", or a ChunkLoadError). */
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      error.message,
    )
  )
}

/** Chunk-load failures get ONLY a Reload: React.lazy caches the rejected
 *  import, so "Try again" would re-render the same failure. Everything else
 *  is rethrown to the outer boundary's default panel (Reload + Try again). */
function chunkErrorFallback(error: Error): React.ReactNode {
  if (!isChunkLoadError(error)) return <Rethrow error={error} />
  return (
    <div role="alert" className="empty-state" data-testid="chunk-error-fallback">
      <h2 style={{ marginTop: 0 }}>This page didn't load.</h2>
      <p className="muted">The app was probably updated while this tab was open. Reload to get the latest version.</p>
      <button type="button" className="primary" onClick={() => window.location.reload()} data-testid="chunk-error-reload">
        Reload page
      </button>
    </div>
  )
}

/** The customer portal — under `/portal/*`, customers only. Same lazy +
 *  Suspense pattern as the staff app; nothing here imports staff pages. */
function PortalRoutes() {
  return (
    <Suspense
      fallback={
        <div className="empty-state" data-testid="page-loading">
          Loading…
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<PortalHome />} />
        <Route path="/profile" element={<PortalProfile />} />
        <Route path="/aws" element={<PortalAws />} />
        <Route path="/team" element={<PortalTeam />} />
        <Route path="/ai-assessment" element={<PortalQuestionnaire module="ai-assessment" />} />
        <Route path="/ftr" element={<PortalQuestionnaire module="ftr" />} />
        <Route path="/wafr" element={<PortalWafr />} />
        <Route path="*" element={<div className="empty-state">Not found.</div>} />
      </Routes>
    </Suspense>
  )
}

/** Everything behind RequireAuth. Lazy pages suspend into one fallback. */
function PageRoutes() {
  return (
    <Suspense
      fallback={
        <div className="empty-state" data-testid="page-loading">
          Loading…
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<CompanyList />} />
        <Route path="/help" element={<Help />} />
        {/* Admin — intentionally not linked from any nav (URL-only). */}
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/admin/audit" element={<Admin />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/companies" element={<AdminCompanies />} />
        <Route path="/admin/playbooks" element={<AdminPlaybooks />} />
        {/* Company hub + company-level features. */}
        <Route path="/companies/:slug" element={<CompanyHub />} />
        <Route path="/companies/:slug/aws-scan" element={<AwsScan />} />
        <Route path="/companies/:slug/aws-inventory" element={<AwsInventory />} />
        <Route path="/companies/:slug/ace/new" element={<AceOpportunityNew />} />
        {/* Legacy post-create onboarding URL — the scan page took its job. */}
        <Route path="/companies/:slug/get-started" element={<Redirect to={({ slug }) => paths.awsScan(slug)} />} />
        <Route path="/companies/:slug/profile" element={<CompanyProfile />} />
        <Route path="/companies/:slug/access" element={<CompanyAccess />} />
        <Route path="/companies/:slug/assistant" element={<AssistantChat />} />
        <Route path="/companies/:slug/research" element={<ResearchReview />} />
        {/* AI Assessment module (the historical workflow). */}
        <Route path="/companies/:slug/ai-assessment" element={<Intake />} />
        <Route path="/companies/:slug/ai-assessment/assistant" element={<Redirect to={moduleAssistantTarget} />} />
        <Route path="/companies/:slug/ai-assessment/transcript-review" element={<TranscriptReview />} />
        {/* The AWS scan runs at the company level now; old URLs land on its page. */}
        <Route path="/companies/:slug/ai-assessment/aws-import" element={<Redirect to={({ slug }) => paths.awsScan(slug)} />} />
        <Route path="/companies/:slug/ai-assessment/use-cases" element={<UseCases />} />
        {/* AI proposals merged into the Proposals page (modal). */}
        <Route path="/companies/:slug/ai-assessment/ai-proposals" element={<Redirect to={({ slug }) => paths.ai(slug, 'proposals')} />} />
        <Route path="/companies/:slug/ai-assessment/proposals" element={<Proposals />} />
        {/* Engagement merged into Submission (ACE + funding tab). Query strings
            are preserved — the ACE-creation page returns here with `?ace_id=`. */}
        <Route
          path="/companies/:slug/ai-assessment/engagement"
          element={<Redirect to={({ slug }) => paths.ai(slug, 'aws-submission')} preserveQuery />}
        />
        {/* Deliverables merged into Submission, which then split by audience —
            the documents live on the CUSTOMER side. */}
        <Route
          path="/companies/:slug/ai-assessment/deliverables"
          element={<Redirect to={({ slug }) => paths.ai(slug, 'customer-submission')} />}
        />
        <Route path="/companies/:slug/ai-assessment/analysis" element={<Analysis />} />
        <Route
          path="/companies/:slug/ai-assessment/customer-submission"
          element={<Submission audience="customer" />}
        />
        <Route
          path="/companies/:slug/ai-assessment/aws-submission"
          element={<Submission audience="aws" />}
        />
        {/* Submission split into Customer / AWS. The AWS side owns the claim
            (ACE, funding, pre-flight, mark-submitted), so a bare `/submission`
            lands there; `?tab=`/`?ace_id=` are preserved. */}
        <Route
          path="/companies/:slug/ai-assessment/submission"
          element={<Redirect to={({ slug }) => paths.ai(slug, 'aws-submission')} preserveQuery />}
        />
        {/* Schema-driven module workspaces. */}
        <Route path="/companies/:slug/wafr" element={<WafrWorkspace />} />
        <Route path="/companies/:slug/wafr/questionnaire" element={<WafrQuestionnaire />} />
        <Route path="/companies/:slug/ftr" element={<FtrWorkspace />} />
        {/* Pre-restructure bookmarks. */}
        <Route path="/assessments/:slug/*" element={<Redirect to={legacyAssessmentRedirectTarget} />} />
        <Route path="/assessments/:slug" element={<Redirect to={legacyAssessmentRedirectTarget} />} />
        <Route path="*" element={<div className="empty-state">Not found.</div>} />
      </Routes>
    </Suspense>
  )
}
